import React, { useState, useEffect, useCallback } from 'react';
import { 
  TrendingUp, 
  ShoppingCart, 
  Users,
  AlertCircle,
  Package,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Utensils,
  Coffee,
  Truck,
  ChevronRight,
  Check,
  X,
  Building2,
  Percent,
  DollarSign,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  BarChart, 
  Bar, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell,
} from 'recharts';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { posService, inventoryService, financeService } from '@/services/api';


// KPI Card Component
const KPICard = ({ title, value, subtitle, change, changeType, icon: Icon, testId, alert, loading }) => (
  <Card data-testid={testId} className="hover:shadow-md transition-shadow">
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={cn(
            "p-2 rounded-lg",
            alert ? "bg-spice-tandoori/10" : "bg-brand-orange/10"
          )}>
            <Icon className={cn("w-4 h-4", alert ? "text-spice-tandoori" : "text-brand-orange")} />
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium">{title}</p>
            {loading ? (
              <div className="flex items-center gap-2 mt-1">
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              </div>
            ) : (
              <>
                <p className="text-xl font-bold font-mono text-brand-charcoal">{value}</p>
                {subtitle && <p className="text-[10px] text-gray-400">{subtitle}</p>}
              </>
            )}
          </div>
        </div>
        {change !== undefined && !loading && (
          <div className={cn(
            'flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded',
            changeType === 'up' ? 'bg-spice-cardamom/10 text-spice-cardamom' : 'bg-spice-tandoori/10 text-spice-tandoori'
          )}>
            {changeType === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(change)}%
          </div>
        )}
        {alert && !loading && (
          <Badge variant="destructive" className="text-[10px]">{alert}</Badge>
        )}
      </div>
    </CardContent>
  </Card>
);

// Order type badge
const OrderTypeBadge = ({ type }) => {
  const config = {
    dine_in: { icon: Utensils, label: 'Dine-in', className: 'bg-brand-orange/10 text-brand-orange' },
    takeaway: { icon: Coffee, label: 'Takeaway', className: 'bg-spice-turmeric/10 text-spice-turmeric' },
    delivery: { icon: Truck, label: 'Delivery', className: 'bg-blue-50 text-blue-600' },
  };
  const { icon: Icon, label, className } = config[type] || config.dine_in;
  return (
    <Badge variant="secondary" className={cn('gap-1 font-normal text-[10px]', className)}>
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
};

// Custom chart tooltip
const ChartTooltip = ({ active, payload, label, currency }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border rounded-lg shadow-lg text-xs">
        <p className="font-semibold text-brand-charcoal mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: {currency} {Number(entry.value).toLocaleString('nb-NO')}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Main Dashboard Component
export function Dashboard() {
  const { 
    outlets: authOutlets, 
    currentOutlet: authCurrentOutlet, 
    switchOutlet,
  } = useAuth();

  const [selectedOutletId, setSelectedOutletId] = useState(authCurrentOutlet?.id || '');
  const [timeRange, setTimeRange] = useState('today');
  const [chartView, setChartView] = useState('weekly');
  const [orderFilter, setOrderFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [inventoryAlerts, setInventoryAlerts] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [error, setError] = useState(null);

  // Use outlets from auth context (real backend UUIDs)
  const accessibleOutlets = React.useMemo(() => authOutlets || [], [authOutlets]);
  
  // Check if "All Outlets" (master view) is selected
  const isMasterView = selectedOutletId === 'all';
  const currentOutlet = isMasterView 
    ? null 
    : (accessibleOutlets.find(o => o.id === selectedOutletId) || authCurrentOutlet);
  const isCorporate = accessibleOutlets.length > 1;

  // Group outlets by country
  const outletsByCountry = React.useMemo(() => {
    const groups = { NO: [], SE: [] };
    accessibleOutlets.forEach(outlet => {
      const country = outlet.address_country || 'NO';
      if (!groups[country]) groups[country] = [];
      groups[country].push(outlet);
    });
    // Sort outlets by name within each country
    Object.keys(groups).forEach(country => {
      groups[country].sort((a, b) => a.name.localeCompare(b.name));
    });
    return groups;
  }, [accessibleOutlets]);

  // Update selected outlet when auth outlet changes
  useEffect(() => {
    if (authCurrentOutlet?.id && !selectedOutletId) {
      setSelectedOutletId(authCurrentOutlet.id);
    }
  }, [authCurrentOutlet?.id, selectedOutletId]);

  // Handle outlet change
  const handleOutletChange = (outletId) => {
    setSelectedOutletId(outletId);
    if (outletId !== 'all') {
      const outlet = accessibleOutlets.find(o => o.id === outletId);
      if (outlet) {
        switchOutlet(outlet);
      }
    }
  };

  // Determine currency based on outlet country (use NOK for master view)
  const currency = isMasterView ? 'NOK' : (currentOutlet?.address_country === 'SE' ? 'SEK' : 'NOK');
  const vatLabel = isMasterView ? 'MVA' : (currentOutlet?.address_country === 'SE' ? 'MOMS' : 'MVA');

  // Format currency helper
  const fmt = (amount) => `${currency} ${Math.round(Number(amount) || 0).toLocaleString('nb-NO')}`;

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    if (!selectedOutletId) return;
    
    try {
      setError(null);
      
      if (isMasterView) {
        // Aggregate data from all outlets
        let aggregatedData = {
          total_sales_today: 0,
          total_orders_today: 0,
          average_order_value: 0,
          total_guests_today: 0,
          available_tables: 0,
          total_tables: 0,
          total_tips_today: 0,
        };
        let allOrders = [];
        let orderCount = 0;
        let totalOrderValue = 0;
        
        // Fetch data from each outlet
        for (const outlet of accessibleOutlets) {
          try {
            const data = await posService.getDashboard(outlet.id);
            aggregatedData.total_sales_today += Number(data?.total_sales_today || 0);
            aggregatedData.total_orders_today += Number(data?.total_orders_today || 0);
            aggregatedData.total_guests_today += Number(data?.total_guests_today || 0);
            aggregatedData.available_tables += Number(data?.available_tables || 0);
            aggregatedData.total_tables += Number(data?.total_tables || 0);
            aggregatedData.total_tips_today += Number(data?.total_tips_today || 0);
            orderCount += Number(data?.total_orders_today || 0);
            totalOrderValue += Number(data?.total_sales_today || 0);
            
            // Fetch orders from each outlet
            try {
              const orders = await posService.getOrders(outlet.id, { date: new Date().toISOString().split('T')[0] });
              allOrders = [...allOrders, ...orders.map(o => ({ ...o, outletName: outlet.name }))];
            } catch (e) { /* ignore */ }
          } catch (e) {
            console.warn(`Could not fetch data for outlet ${outlet.name}:`, e);
          }
        }
        
        // Calculate average
        aggregatedData.average_order_value = orderCount > 0 ? totalOrderValue / orderCount : 0;
        
        setDashboardData(aggregatedData);
        setRecentOrders(allOrders.slice(0, 8));
      } else {
        // Single outlet data
        const data = await posService.getDashboard(selectedOutletId);
        setDashboardData(data);
        
        // Fetch recent orders
        try {
          const orders = await posService.getOrders(
            selectedOutletId,
            { date: new Date().toISOString().split('T')[0] }
          );
          setRecentOrders(orders.slice(0, 6));
        } catch (orderErr) {
          console.warn('Could not fetch recent orders:', orderErr);
        }
      }
      
      // Fetch inventory alerts (for first outlet or selected outlet)
      const alertOutletId = isMasterView ? accessibleOutlets[0]?.id : selectedOutletId;
      if (alertOutletId) {
        try {
          const alerts = await inventoryService.getStockAlerts(alertOutletId);
          setInventoryAlerts(alerts || []);
        } catch (alertErr) {
          console.warn('Could not fetch inventory alerts:', alertErr);
          setInventoryAlerts([]);
        }
      }
      
      // Fetch pending approvals (expenses)
      try {
        const outlet = accessibleOutlets[0];
        if (outlet?.tenant_id) {
          const expenses = await financeService.getPendingApprovals(outlet.tenant_id, {
            outletId: isMasterView ? undefined : selectedOutletId,
            status: 'pending'
          });
          setPendingApprovals((expenses || []).slice(0, 5));
        }
      } catch (approvalErr) {
        console.warn('Could not fetch pending approvals:', approvalErr);
        setPendingApprovals([]);
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedOutletId, accessibleOutlets, isMasterView]);

  useEffect(() => {
    setLoading(true);
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
  };

  // Get current date in Norwegian format
  const currentDate = new Date().toLocaleDateString('nb-NO', { 
    weekday: 'long', 
    day: 'numeric', 
    month: 'long',
    year: 'numeric'
  });

  // Generate mock weekly/hourly data for charts (until we have historical APIs)
  const weeklyTrend = [
    { day: 'Man', net: Math.round((dashboardData?.total_sales_today || 0) * 0.8), vat: Math.round((dashboardData?.total_sales_today || 0) * 0.2) },
    { day: 'Tir', net: Math.round((dashboardData?.total_sales_today || 0) * 0.7), vat: Math.round((dashboardData?.total_sales_today || 0) * 0.17) },
    { day: 'Ons', net: Math.round((dashboardData?.total_sales_today || 0) * 0.9), vat: Math.round((dashboardData?.total_sales_today || 0) * 0.22) },
    { day: 'Tor', net: Math.round((dashboardData?.total_sales_today || 0) * 1.1), vat: Math.round((dashboardData?.total_sales_today || 0) * 0.27) },
    { day: 'Fre', net: Math.round((dashboardData?.total_sales_today || 0) * 1.4), vat: Math.round((dashboardData?.total_sales_today || 0) * 0.35) },
    { day: 'Lør', net: Math.round((dashboardData?.total_sales_today || 0) * 1.6), vat: Math.round((dashboardData?.total_sales_today || 0) * 0.4) },
    { day: 'I dag', net: Number(dashboardData?.total_sales_today || 0), vat: Math.round((dashboardData?.total_sales_today || 0) * 0.25) },
  ];

  const hourlyData = [
    { hour: '11', sales: Math.round((dashboardData?.total_sales_today || 0) * 0.05) },
    { hour: '12', sales: Math.round((dashboardData?.total_sales_today || 0) * 0.12) },
    { hour: '13', sales: Math.round((dashboardData?.total_sales_today || 0) * 0.15) },
    { hour: '14', sales: Math.round((dashboardData?.total_sales_today || 0) * 0.08) },
    { hour: '15', sales: Math.round((dashboardData?.total_sales_today || 0) * 0.05) },
    { hour: '16', sales: Math.round((dashboardData?.total_sales_today || 0) * 0.06) },
    { hour: '17', sales: Math.round((dashboardData?.total_sales_today || 0) * 0.12) },
    { hour: '18', sales: Math.round((dashboardData?.total_sales_today || 0) * 0.18) },
    { hour: '19', sales: Math.round((dashboardData?.total_sales_today || 0) * 0.12) },
    { hour: '20', sales: Math.round((dashboardData?.total_sales_today || 0) * 0.05) },
    { hour: '21', sales: Math.round((dashboardData?.total_sales_today || 0) * 0.02) },
  ];

  // Category breakdown from payment data
  const categoryBreakdown = [
    { name: 'Kort', value: Number(dashboardData?.card_sales || 0), color: '#FF6900' },
    { name: 'Kontant', value: Number(dashboardData?.cash_sales || 0), color: '#FDB913' },
    { name: 'Vipps', value: Number(dashboardData?.vipps_sales || 0), color: '#27AE60' },
    { name: 'Faktura', value: Number(dashboardData?.invoice_sales || 0), color: '#32373C' },
  ].filter(c => c.value > 0);

  // VAT breakdown
  const totalSales = Number(dashboardData?.total_sales_today || 0);
  const estimatedVat = totalSales * 0.2; // Approximate 20% VAT
  const netSales = totalSales - estimatedVat;

  const vatBreakdown = [
    { type: 'Dine-in', net: netSales * 0.7, vatRate: 0.25, vat: netSales * 0.7 * 0.25, gross: netSales * 0.7 * 1.25 },
    { type: 'Takeaway', net: netSales * 0.25, vatRate: 0.15, vat: netSales * 0.25 * 0.15, gross: netSales * 0.25 * 1.15 },
    { type: 'Delivery', net: netSales * 0.05, vatRate: 0.15, vat: netSales * 0.05 * 0.15, gross: netSales * 0.05 * 1.15 },
  ];

  // Filter orders
  const filteredOrders = orderFilter === 'all' 
    ? recentOrders 
    : recentOrders.filter(o => o.order_type === orderFilter);

  return (
    <div data-testid="dashboard-page" className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-xl sm:text-2xl text-brand-charcoal">Dashboard</h1>
          <p className="text-gray-500 text-xs sm:text-sm">
            {isMasterView 
              ? `All Outlets (${accessibleOutlets.length}) • ${currentDate}`
              : `${currentOutlet?.name || 'Select outlet'}, ${currentOutlet?.address_city || ''} • ${currentDate}`
            }
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Outlet Selector with Grouped Options */}
          <Select value={selectedOutletId} onValueChange={handleOutletChange}>
            <SelectTrigger data-testid="outlet-selector-dashboard" className="w-full sm:w-[220px] text-sm">
              <Building2 className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Select outlet">
                {isMasterView ? '🌍 All Outlets (Master View)' : (currentOutlet?.name || 'Select outlet')}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {/* Master View Option */}
              {isCorporate && (
                <>
                  <SelectItem value="all" className="font-semibold">
                    <span className="flex items-center gap-2">
                      <span>🌍</span>
                      All Outlets (Master View)
                    </span>
                  </SelectItem>
                  <div className="border-t my-1" />
                </>
              )}
              
              {/* Norway Outlets */}
              {outletsByCountry.NO?.length > 0 && (
                <>
                  <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                    🇳🇴 Norway ({outletsByCountry.NO.length})
                  </div>
                  {outletsByCountry.NO.map((outlet) => (
                    <SelectItem key={outlet.id} value={outlet.id}>
                      <span className="flex items-center gap-2">
                        <span className="text-[10px] text-gray-400">{outlet.address_city}</span>
                        {outlet.name}
                      </span>
                    </SelectItem>
                  ))}
                </>
              )}
              
              {/* Sweden Outlets */}
              {outletsByCountry.SE?.length > 0 && (
                <>
                  <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50 mt-1">
                    🇸🇪 Sweden ({outletsByCountry.SE.length})
                  </div>
                  {outletsByCountry.SE.map((outlet) => (
                    <SelectItem key={outlet.id} value={outlet.id}>
                      <span className="flex items-center gap-2">
                        <span className="text-[10px] text-gray-400">{outlet.address_city}</span>
                        {outlet.name}
                      </span>
                    </SelectItem>
                  ))}
                </>
              )}
            </SelectContent>
          </Select>

          {/* Time Range */}
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[100px] sm:w-[120px] text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">I dag</SelectItem>
              <SelectItem value="yesterday">I går</SelectItem>
              <SelectItem value="week">Denne uken</SelectItem>
              <SelectItem value="month">Denne mnd</SelectItem>
            </SelectContent>
          </Select>

          <Button 
            variant="outline" 
            size="icon" 
            className="h-9 w-9"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* Data Scope Indicator */}
      {isCorporate && (
        <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 px-3 py-1.5 rounded-lg">
          <Building2 className="w-3 h-3" />
          {isMasterView ? (
            <>
              <span><strong>Master View</strong> • Aggregated data from all outlets</span>
              <Badge className="bg-brand-orange text-[10px]">
                {accessibleOutlets.length} outlets
              </Badge>
            </>
          ) : (
            <>
              <span>Corporate view • Showing data for: <strong>{currentOutlet?.name}</strong></span>
              <Badge variant="outline" className="text-[10px]">
                {accessibleOutlets.length} outlets accessible
              </Badge>
            </>
          )}
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <div>
            <p className="text-sm font-medium text-red-800">{error}</p>
            <p className="text-xs text-red-600">Please try refreshing or check your connection.</p>
          </div>
          <Button variant="outline" size="sm" onClick={handleRefresh} className="ml-auto">
            Retry
          </Button>
        </div>
      )}

      {/* KPI Cards - Top Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPICard 
          testId="kpi-gross-sales"
          title="Brutto salg" 
          value={fmt(dashboardData?.total_sales_today || 0)}
          subtitle={`Inkl. ${vatLabel}`}
          icon={TrendingUp}
          loading={loading}
        />
        <KPICard 
          testId="kpi-completed-orders"
          title="Ordrer i dag" 
          value={dashboardData?.completed_orders_today || 0}
          icon={ShoppingCart}
          loading={loading}
        />
        <KPICard 
          testId="kpi-avg-ticket"
          title="Snittbong" 
          value={fmt(dashboardData?.average_ticket || 0)}
          icon={DollarSign}
          loading={loading}
        />
        <KPICard 
          testId="kpi-guests"
          title="Gjester i dag" 
          value={dashboardData?.total_guests_today || 0}
          icon={Users}
          loading={loading}
        />
        <KPICard 
          testId="kpi-tables"
          title="Ledige bord" 
          value={`${dashboardData?.free_tables || 0}/${dashboardData?.total_tables || 0}`}
          subtitle={`${dashboardData?.occupied_tables || 0} opptatt`}
          icon={Utensils}
          loading={loading}
        />
        <KPICard 
          testId="kpi-tips"
          title="Tips i dag" 
          value={fmt(dashboardData?.tips_today || 0)}
          icon={Percent}
          loading={loading}
        />
      </div>

      {/* VAT Summary Card */}
      <Card>
        <CardHeader className="py-3 px-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">{vatLabel}-oversikt</CardTitle>
            <Badge variant="outline" className="font-mono text-xs">
              {currentOutlet?.address_country === 'NO' ? 'Norway' : 'Sweden'} • {currency}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="grid grid-cols-4 gap-4">
            {vatBreakdown.map((row) => (
              <div key={row.type} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-600">{row.type}</span>
                  <Badge variant="secondary" className="text-[10px]">{(row.vatRate * 100)}%</Badge>
                </div>
                <p className="text-lg font-bold font-mono">{fmt(row.gross)}</p>
                <p className="text-[10px] text-gray-400">
                  Netto: {fmt(row.net)} • {vatLabel}: {fmt(row.vat)}
                </p>
              </div>
            ))}
            <div className="p-3 bg-brand-orange/5 rounded-lg border border-brand-orange/20">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-brand-orange">Totalt</span>
              </div>
              <p className="text-lg font-bold font-mono text-brand-charcoal">{fmt(totalSales)}</p>
              <p className="text-[10px] text-gray-500">
                {vatLabel}: {fmt(estimatedVat)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Sales Trend */}
        <Card className="lg:col-span-2">
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">Omsetningstrend</CardTitle>
              <Tabs value={chartView} onValueChange={setChartView}>
                <TabsList className="h-7">
                  <TabsTrigger value="weekly" className="text-xs px-2 h-6">7 dager</TabsTrigger>
                  <TabsTrigger value="hourly" className="text-xs px-2 h-6">I dag</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="h-[220px]">
              {loading ? (
                <div className="h-full flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-300" />
                </div>
              ) : chartView === 'weekly' ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weeklyTrend} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                    <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip content={<ChartTooltip currency={currency} />} />
                    <Bar dataKey="net" name="Netto" fill="#FF6900" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="vat" name={vatLabel} fill="#FDB913" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={hourlyData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                    <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}:00`} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip content={<ChartTooltip currency={currency} />} />
                    <Line type="monotone" dataKey="sales" name="Salg" stroke="#FF6900" strokeWidth={2} dot={{ fill: '#FF6900', r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Payment Method Breakdown */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm font-semibold">Betalingsmetoder</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="h-[140px]">
              {loading ? (
                <div className="h-full flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
                </div>
              ) : categoryBreakdown.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryBreakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={35}
                      outerRadius={60}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {categoryBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => fmt(value)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                  Ingen salgsdata ennå
                </div>
              )}
            </div>
            <div className="space-y-1.5 mt-2">
              {categoryBreakdown.map((cat, index) => (
                <div key={index} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded" style={{ backgroundColor: cat.color }} />
                    <span className="text-gray-600">{cat.name}</span>
                  </div>
                  <span className="font-mono font-medium">{fmt(cat.value)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Section: Orders, Inventory, Approvals */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent Orders */}
        <Card className="lg:col-span-2">
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">Siste ordrer</CardTitle>
              <div className="flex items-center gap-2">
                <Select value={orderFilter} onValueChange={setOrderFilter}>
                  <SelectTrigger className="h-7 w-[100px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle</SelectItem>
                    <SelectItem value="dine_in">Dine-in</SelectItem>
                    <SelectItem value="takeaway">Takeaway</SelectItem>
                    <SelectItem value="delivery">Delivery</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" className="text-brand-orange text-xs h-7">
                  Vis alle <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50/50">
                  <TableHead className="text-xs py-2">Ordre</TableHead>
                  <TableHead className="text-xs py-2">Tid</TableHead>
                  <TableHead className="text-xs py-2">Type</TableHead>
                  <TableHead className="text-xs py-2 text-right">Total</TableHead>
                  <TableHead className="text-xs py-2">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-gray-300 mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : filteredOrders.length > 0 ? (
                  filteredOrders.map((order) => (
                    <TableRow key={order.id} className="hover:bg-gray-50/50">
                      <TableCell className="py-2 font-mono text-xs">#{order.order_number}</TableCell>
                      <TableCell className="py-2 text-xs text-gray-600">
                        {order.order_time ? order.order_time.slice(0, 5) : '-'}
                      </TableCell>
                      <TableCell className="py-2"><OrderTypeBadge type={order.order_type} /></TableCell>
                      <TableCell className="py-2 text-right font-mono text-xs font-semibold">
                        {fmt(order.total)}
                      </TableCell>
                      <TableCell className="py-2">
                        <Badge 
                          variant={order.status === 'paid' ? 'default' : 'secondary'}
                          className={cn(
                            "text-[10px]",
                            order.status === 'paid' && 'bg-spice-cardamom'
                          )}
                        >
                          {order.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-gray-400 text-sm">
                      Ingen ordrer i dag ennå
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Inventory Alerts */}
          <Card>
            <CardHeader className="py-3 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold">Lagervarsler</CardTitle>
                <Badge variant="secondary" className={cn(
                  "text-[10px]",
                  inventoryAlerts.length > 0 ? "bg-spice-tandoori/10 text-spice-tandoori" : "bg-gray-100 text-gray-500"
                )}>
                  {inventoryAlerts.length} varer
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2">
              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
                </div>
              ) : inventoryAlerts.length > 0 ? (
                inventoryAlerts.slice(0, 3).map((item) => (
                  <div 
                    key={item.raw_material_id}
                    className={cn(
                      'p-2 rounded-lg border text-xs',
                      item.severity === 'critical' 
                        ? 'bg-spice-tandoori/5 border-spice-tandoori/20' 
                        : 'bg-spice-turmeric/5 border-spice-turmeric/20'
                    )}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">{item.raw_material_name}</span>
                      <Badge 
                        variant={item.severity === 'critical' ? 'destructive' : 'secondary'}
                        className={cn("text-[10px]", item.severity !== 'critical' && 'bg-spice-turmeric')}
                      >
                        {item.severity === 'critical' ? 'Kritisk' : 'Lav'}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">
                        {Number(item.current_quantity).toFixed(1)} / {Number(item.reorder_point).toFixed(1)} {item.unit}
                      </span>
                      <Progress 
                        value={(Number(item.current_quantity) / Number(item.reorder_point)) * 100} 
                        className="w-16 h-1" 
                      />
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-gray-400 text-xs">
                  Ingen lagervarsler
                </div>
              )}
              <Button variant="outline" size="sm" className="w-full text-xs h-8 gap-1">
                <Package className="w-3 h-3" />
                Opprett innkjøpsordre
              </Button>
            </CardContent>
          </Card>

          {/* Pending Approvals */}
          <Card>
            <CardHeader className="py-3 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold">Godkjenninger</CardTitle>
                <Badge className={cn(
                  "text-[10px]",
                  pendingApprovals.length > 0 ? "bg-brand-orange" : "bg-gray-400"
                )}>
                  {pendingApprovals.length}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2">
              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
                </div>
              ) : pendingApprovals.length > 0 ? (
                pendingApprovals.map((approval) => (
                  <div key={approval.id} className="p-2 bg-gray-50 rounded-lg text-xs">
                    <div className="flex items-start justify-between mb-1">
                      <div>
                        <span className="font-mono text-[10px] text-gray-400">
                          EXP-{String(approval.id).slice(-6).toUpperCase()}
                        </span>
                        <p className="font-medium">{approval.description || approval.category}</p>
                        <p className="text-[10px] text-gray-500">
                          {approval.created_by_name || 'Staff'} • {
                            approval.created_at 
                              ? new Date(approval.created_at).toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit' })
                              : '-'
                          }
                        </p>
                      </div>
                      <span className="font-mono font-semibold">{fmt(approval.amount)}</span>
                    </div>
                    <div className="flex gap-1 mt-2">
                      <Button size="sm" className="flex-1 h-6 text-[10px] bg-spice-cardamom hover:bg-spice-cardamom/90 gap-1">
                        <Check className="w-3 h-3" /> Godkjenn
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1 h-6 text-[10px] gap-1">
                        <X className="w-3 h-3" /> Avslå
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-gray-400 text-xs">
                  Ingen ventende godkjenninger
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
